import React, { useEffect } from 'react';
import { Route, useHistory } from 'react-router-dom';
import { getKey, isUserLoggedIn } from '../../libs/utils/storageManager';
import { getLocalStorage } from '../../libs/utils/cacheHandler';
import { KeycloakPostLogin } from './Auth/KeycloakPostLogin';
import { SignUpIndividualWrapper } from './SignUp/Individual/SignUpIndividualWrapper';
import { BusinessUserSignupWrapper } from './SignUp/Business/BusinessUserSignupWrapper';
import { JointTenancySignUpWrapper } from './SignUp/JoinTenancy/JointTenancySignUpWrapper';
import { PartnerAgreement } from './Partner/Onboarding/PartnerAgreement';
import { JointTenancyCoOwnerSignUpWrapper } from './SignUp/JoinTenancy/JointTenancyCoOwnerSignup/JointTenancyCoOwnerSignUpWrapper';
import { Logger } from '../../libs/utils/logger';

/**
 * Author : Athul PP
 * Component Name : "KycRoute"
 * Component Description : This component is responsible for rendering the KYC routes.
 * 
 */
export const KycRoute: React.FC = () => {
  const history = useHistory();
  const signupFlow = getLocalStorage('signUpFlow');
  const isLoggedIn = isUserLoggedIn();

  const isUserInfoAvailable =
    getKey('isUserDetailsAvailable') === 'TRUE' || getKey('accountStatus') === 'false';

  /**
   * If the signup flow is active and the user is logged in,
   * check for a stored KYC path in localStorage.
   * If present, clean the path and redirect the user.
   * Otherwise, redirect to the default post-auth route.
   */
  useEffect(() => {
    if (signupFlow === 'true' && isLoggedIn) {
      const kycPath = getLocalStorage('kycPath');
      if (kycPath) {
        const redirectPath = kycPath.replace(/^"+|"+$/g, '').replace(/^\/+/, '');
        if (redirectPath) {
          history.push(redirectPath);
        }
      }
    }
  }, []);

 /**
   * Attempt to redirect the user based on a stored KYC redirection route
   * if they are in the signup flow and already logged in.
   */
  useEffect(() => {
    try {
      const redirectionRoute = getLocalStorage('kycRedirectionRoute');
      if (signupFlow === 'true' && isLoggedIn && redirectionRoute) {
        const sanitizedRoute = redirectionRoute.replace(/^['"]|['"]$/g, '');
        if (sanitizedRoute) {
          history.push(sanitizedRoute);
        }
      }
    } catch (error) {
      Logger.error('KycRoutes.tsx','error', error);
    }
  }, [signupFlow, isLoggedIn, history]);

  // Helper to render conditional KYC route
  const renderKycRoute = (path: string, component: JSX.Element, key: string) => (
    isUserInfoAvailable ? (
      <Route path={path} key={key}>
        {component}
      </Route>
    ) : (
      <Route exact path={path} key={`${key}-fallback`}>
        <KeycloakPostLogin id="keycloak-post-login" key="userInfo" />
      </Route>
    )
  );

  return (
    <>
      {renderKycRoute('/signup/joint_tenancy/kyc', <JointTenancySignUpWrapper />, 'joint-tenancy-kyc')}
      {renderKycRoute('/signup/joint_tenancy_co_owner/kyc',<JointTenancyCoOwnerSignUpWrapper/>, 'joint-tenancy-co-owner-kyc')}
      {renderKycRoute('/signup/individual/kyc', <SignUpIndividualWrapper />, 'individual-kyc')}
      {renderKycRoute('/signup/business/kyc', <BusinessUserSignupWrapper />, 'business-kyc')}
      {renderKycRoute('/signup/partner/welcome', <PartnerAgreement id="partner-aggrement-details" key="partner-aggrement-layout" />, 'partner-welcome')}
    </>
  );
};
